import type { ICRMEventsContext } from '@fhs-legacy/frontend/src/state/crm-events';

import type { IPipelineBaseStep, PaymentPipelineResult, PipelineStepContext } from '..';
import { Logger } from '..';
import {
  type PaymentFlowStore,
  usePaymentFlowStore,
} from '../../state/payment-stores/payment-flow-store';
import { isPaymentCancelled } from '../payment-cancelled-codes';

export class PaymentPipeline {
  private steps: IPipelineBaseStep[];
  private context: PipelineStepContext;
  private logger = new Logger('PaymentPipeline');

  constructor(steps: IPipelineBaseStep[], context: PipelineStepContext) {
    this.steps = steps;
    this.context = { ...context, errors: context.errors || [] };
  }

  setCRMLogger(logger: ICRMEventsContext['logRBIEvent']): void {
    this.logger.setCRMLogger(logger);
  }

  // Map step names to payment flow states
  private mapStepToState(stepName: string): PaymentFlowStore['currentState'] {
    switch (stepName) {
      case 'EncryptionStep':
        return 'encrypting';
      case 'ValidateCartStep':
        return 'validatingCart';
      case 'CheckServiceModeStep':
        return 'serviceModeCheck';
      case 'CommitOrderStep':
        return 'committing';
      case 'VaultCardStep':
        return 'vaulting';

      default:
        return 'idle';
    }
  }

  async execute(): Promise<PaymentPipelineResult> {
    this.logger.log(`Starting pipeline with ${this.steps.length} steps`);

    for (const [i, step] of this.steps.entries()) {
      this.logger.log(`Executing step ${i + 1}/${this.steps.length}: ${step.name}`);

      // Update the payment flow state before executing each step
      const stepState = this.mapStepToState(step.name);
      usePaymentFlowStore.getState().setCurrentState(stepState);

      try {
        const result = await step.execute(this.context);

        // Merge data into context
        if (result.data) {
          this.context = {
            ...this.context,
            ...result.data,
          } as PipelineStepContext;
        }

        // Add error to errors array if present
        if (result.error) {
          this.context.errors.push(result.error);
        }

        // Stop pipeline (on first error) if step failed and requested stop
        if (!result.success && result.shouldStop) {
          const cancelled = isPaymentCancelled(result.error?.code);

          this.logger.log(
            cancelled
              ? `Pipeline stopped at step: ${step.name} (user cancelled)`
              : `Pipeline stopped at step: ${step.name}`,
            result.error
          );

          if (!cancelled) {
            this.logger.logCRMEvent('StepFailed', {
              stepName: step.name,
              stepIndex: i + 1,
              totalSteps: this.steps.length,
              errorCode: result.error?.code,
              errorMessage: result.error?.message,
              retryable: !!result.error?.retryFromStep,
              paymentMethod: this.context.paymentMethod,
            });
          }

          usePaymentFlowStore.getState().setCurrentState(cancelled ? 'idle' : 'error');

          return {
            success: false,
            error: result.error,
            failedStep: step.name,
            context: this.context,
          };
        }

        this.logger.log(`Step ${step.name} completed successfully`);
      } catch (error) {
        // Unexpected error during step execution
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        this.logger.logError(`Unexpected error in step ${step.name}:`, error);

        this.logger.logCRMEvent('UnexpectedPipelineError', {
          stepName: step.name,
          stepIndex: i + 1,
          totalSteps: this.steps.length,
          errorMessage,
          paymentMethod: this.context.paymentMethod,
        });

        // Reset state to error
        usePaymentFlowStore.getState().setCurrentState('error');

        return {
          success: false,
          error: {
            code: 'UNEXPECTED_ERROR',
            message: errorMessage,
            userMessage:
              'An unexpected error occurred. Please try again. Error code: (Unexpected Pipeline Error)',
          },
          failedStep: step.name,
          context: this.context,
        };
      }
    }

    this.logger.log('Pipeline completed successfully');

    // Reset to success state
    usePaymentFlowStore.getState().setCurrentState('success');

    return {
      success: true,
      context: this.context,
    };
  }

  // Get current context for debugging
  getContext(): PipelineStepContext {
    return this.context;
  }

  // Get steps for debugging
  getSteps(): IPipelineBaseStep[] {
    return this.steps;
  }
}
